/**
 * Display-only cleanup for assistant answers with duplicated compose tails /
 * repeated <render> bodies. Does not change persisted transcript text.
 *
 * Caller: InsightsAnswer. Thread ins-19c91717… had full visual answer
 * (H2 + table + bars + actions) emitted twice — user: "Lot formatting problems
 * for render/GenUI blocks."
 */

/** Normalize whitespace for fuzzy duplicate checks. */
function norm(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

/**
 * If the same markdown H2 appears twice and the later section is largely a
 * repeat of the earlier one, drop from the second heading onward.
 */
export function collapseDuplicateH2Sections(text: string): string {
  const re = /^## (.+)$/gm;
  const matches = [...text.matchAll(re)];
  if (matches.length < 2) return text;

  const firstIndexByTitle = new Map<string, number>();
  for (const m of matches) {
    if (m.index == null) continue;
    const title = m[1]!.trim();
    const prev = firstIndexByTitle.get(title);
    if (prev == null) {
      firstIndexByTitle.set(title, m.index);
      continue;
    }

    const between = text.slice(prev, m.index);
    const after = text.slice(m.index);
    const betweenBody = norm(between.replace(/^## .+$/m, ""));
    const afterBody = norm(after.replace(/^## .+$/m, ""));
    if (!afterBody) return text.slice(0, m.index).trimEnd();

    const probe = afterBody.slice(0, Math.min(480, afterBody.length));
    if (
      betweenBody.includes(probe) ||
      afterBody.includes(betweenBody.slice(0, Math.min(480, betweenBody.length))) ||
      (betweenBody.length > 200 &&
        afterBody.length > 200 &&
        betweenBody.slice(0, 200) === afterBody.slice(0, 200))
    ) {
      return text.slice(0, m.index).trimEnd();
    }
  }
  return text;
}

/**
 * Drop later <render>…</render> blocks whose body matches an earlier one
 * (exact after trim). Keeps surrounding prose.
 */
export function dedupeIdenticalRenderBlocks(text: string): string {
  const seen = new Set<string>();
  return text.replace(/<render>([\s\S]*?)<\/render>/gi, (full, body: string) => {
    const key = body.trim();
    if (!key) return full;
    if (seen.has(key)) return "";
    seen.add(key);
    return full;
  });
}

/** Apply display-only answer cleanups before segment parse. */
export function prepareAnswerTextForDisplay(text: string): string {
  let out = collapseDuplicateH2Sections(text);
  out = dedupeIdenticalRenderBlocks(out);
  out = out.replace(/\n{3,}/g, "\n\n");
  return out;
}
